<template>
	<section data-testid="class-schedules-component">
		<h1 class="h2 text-xs-center" v-if="prospect.headingPrefix && prospect.clubName">{{ prospect.headingPrefix }} {{ prospect.clubName }}</h1>
		<div :class="{'inline-mode': isInlineMode}">
			<div class="inline-mode-scroller" :style="[isInlineMode ? {'max-height': inlineMode.maxHeight + 'px'} : null]">

				<div role="region" aria-label="Filters and Tools">
					<!-- Page Header Buttons -->
					<div class="hidden-print panel-wrapper">
						<div :class="{'filters-section': isProspectMode}">
							<div class="container">
								<div class="row" v-if="urlError">
									<div class="col-xs-12">
										<div class="alert alert-warning" role="alert">
											<p>You have selected the maximum number of search filters allowed.</p>
										</div>
									</div>
								</div>

								<!-- Skip Link for Screen Reader and Keyboard Users -->
								<button v-if="eventDataDay && isDayView" @click="skipToTime" class="btn btn-link p-x-grid p-y-sm m-t-1 sr-only sr-only-focusable">
									{{ skipLinkText }}
								</button>

								<!-- Prospect Mode Location Select and More/Less Filters Toggle -->
								<div class="row" v-if="isProspectMode && prospect.clubs.length">
									<div class="col-md-4 col-sm-12 col-xs-12" v-show="!prospect.isClubPage">
										<select class="c-select" v-model="prospect.clubName" @change="handleProspectClubChanged">
											<optgroup v-for="group in prospect.clubs" :label="group.name" :key="group.name">
												<option v-for="option in group.choices" :value="option.value" :key="option.value" :disabled="!option.enabled">
													{{ option.name }}
												</option>
											</optgroup>
										</select>
									</div>
									<div v-if="filterTypes.length > 0" class="col-md-4 col-md-offset-4 col-sm-12 col-xs-12" :class="{'col-md-offset-8': prospect.isClubPage}">
										<button class="btn btn-primary btn-xs pull-md-right m-y-sm" data-toggle="collapse" data-target="#filtersSub" aria-controls="filtersSub" data-label-collapsed="More Filters" data-label-expanded="Less Filters" aria-expanded="false">More Filters</button>
									</div>
								</div>

								<div class="page-head page-head-compact" v-if="!isInlineMode && !isMobileApp">
									<button class="btn btn-primary btn-xs" data-testid="filtersBtn" data-toggle="collapse" data-target="#filtersSub" aria-controls="filtersSub" data-label-collapsed="Filters" data-label-expanded="Hide Filters" aria-expanded="false">Filters</button>
									<div class="page-head-buttons">
										<button class="btn btn-link btn-xs" data-testid="favoritesBtn" data-toggle="collapse" data-target="#favoritesSub" aria-controls="favoritesSub" data-label-collapsed="<span class='ico-heart icon-adjust-horiz icon-adjust-vert'></span> Favorites" data-label-expanded="<span class='ico-heart icon-adjust-horiz icon-adjust-vert'></span> Hide Favorites" aria-expanded="false"><span class="ico-heart icon-adjust-horiz icon-adjust-vert"></span> Favorites</button>
										<button class="btn btn-link btn-xs hidden-sm-down" data-testid="dayViewBtn" v-if="isWeekView && showViewToggles" @click="setViewMode('day')">Day View</button>
										<button class="btn btn-link btn-xs hidden-sm-down" data-testid="weekViewBtn" v-if="isDayView && showViewToggles" @click="setViewMode('week')">Week View</button>
										<button class="btn btn-link btn-xs hidden-sm-down" @click="triggerPrint">Print Week</button>
										<a class="btn btn-link btn-xs" data-testid="helpLink" :href="classSchedulesFaqUrl">Help</a>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Favorites -->
					<FavoritesSub
						v-if="!isInlineMode && !isMobileApp"
						id="favoritesSub"
						data-testid="favoritesSub"
						ref="favoritesSub"
						:userKnown="userKnown"
						:loginUrl="loginUrl"
						:favorites="favorites"
						@loaded="handleLoadSavedSearch"
						@updated="handleFavoritesUpdated"
						class="collapse"
					/>

					<!-- Filters -->
					<SearchFilters
						v-if="facetData && filterTypes.length > 0 && (!isInlineMode || (isInlineMode && isProspectMode)) && !isMobileApp"
						filterPage="classSchedules"
						id="filtersSub"
						ref="filtersSub"
						:filterTypes="filterTypes"
						:facets="facetData"
						:queryFilters="queryFilters"
						:savedSearchName="savedSearchName"
						:savedSearchFilters="savedSearchFilters"
						prefix="ltf-class-schedules-filter-"
						@changed="handleFiltersChanged"
						@rebuildQueryFilters="handleRebuildQueryFilters"
						class="collapse"
					/>

					<!-- Chiclets -->
					<Chiclets
						v-if="eventData && (!isInlineMode || (isInlineMode && isProspectMode)) && !isMobileApp"
						:selected="searchChiclets"
						:savedSearchName="savedSearchName"
						:savedSearchFilters="savedSearchFilters"
						:allowFavorites="!isProspectMode"
						@removed="handleRemovedChiclet"
						@clearAllChiclets="handleClearAllChiclets"
						@createFavorite="handleCreateFavorite"
						@savedSearchRemoved="handleFavoritesUpdated"
					/>

				</div>

				<div class="planner" :class="{'planner-week': isWeekView}">

					<!-- Date Bar (Date Nav) -->
					<DateBarSub
						v-if="days.length > 0 && selectedDate"
						:viewMode="viewMode"
						:days="days"
						:selectedDate="selectedDate"
						@dayChanged="handleDayChanged"
						@weekChanged="handleWeekChanged"
					/>

					<!-- Day View -->
					<DayViewSub
						v-if="eventDataDay && isDayView"
						:day="eventDataDay"
						:isProspectMode="isProspectMode"
						ref="dayView"
						@classClicked="handleClassClicked"
					/>

					<!-- Week View -->
					<WeekViewSub
						v-if="days.length > 0 && isWeekView"
						:days="days"
						:selectedDate="selectedDate"
						:isInlineMode="isInlineMode"
						:isProspectMode="isProspectMode"
						:isPrintingWeekView="isPrintingWeekView"
						@classClicked="handleClassClicked"
					/>

					<div class="disclaimer">Classes and times are subject to change.</div>
				</div>

				<!-- Loading Spinner -->
				<div role="alert" aria-live="assertive">
					<transition name="fade" appear>
						<div class="spinner-overlay" data-testid="spinner" v-show="isWaiting && !isPrintingWeekView">
							<p class="sr-only">Classes are loading…</p>
							<LoadingSpinner />
						</div>
					</transition>
				</div>

				<!-- Printing Overlay -->
				<div role="alert" aria-live="assertive">
					<transition name="fade" appear>
						<div class="spinner-overlay print-week-overlay" data-testid="printSpinner" v-show="isPrintingWeekView">
							<p>Printing&hellip;</p>
						</div>
					</transition>
				</div>

			</div>
		</div>

		<!-- Class Summary Modal -->
		<Modal ref="classSummaryModal" ariaLabelled="classSummaryModalTitle">
			<template #title>{{ classSummary.className }}</template>
			<template #default>
				{{ classSummary.classDescription }}
			</template>
		</Modal>

	</section>
</template>

<script>
import _ from 'lodash';
import { defineComponent } from 'vue';
import moment from 'moment';
import { baseComponentMixin } from '@/mixins/baseComponent.mixin';
import LoadingSpinner from '@/components/schreg-common/LoadingSpinner.vue';
import SearchFilters from '@/components/schreg-common/SearchFilters.vue';
import Chiclets from '@/components/schreg-common/Chiclets.vue';
import Modal from '@/components/schreg-common/Modal.vue';
import DateBarSub from './subs/DateBarSub.vue';
import DayViewSub from './subs/DayViewSub.vue';
import WeekViewSub from './subs/WeekViewSub.vue';
import FavoritesSub from './subs/FavoritesSub.vue';
import { UxSchedulesApi } from '@/apis/uxSchedules.api';
import { momentProvider } from '@/services/clock.service';
import { StateService } from '@/services/state.service';
import { SessionService } from '@/services/session.service';
import { FilteringService, FilteringServicePages } from '@/services/filtering.service';
import { FilterTypes } from '@/services/filtering.service.config';
import { Helpers } from '@/common/helpers';
import { NotificationService } from '@/services/notification.service';
import { FrameworkService } from '@/services/framework.service';
import { AnalyticsService } from '@/services/analytics.service';

export default defineComponent({
	name: 'ClassSchedulesComponent',
	config: {
		key: 'schreg.classSchedules',
		devPath: 'class-schedules-all',
		exported: true
	},
	components: {
		SearchFilters,
		Chiclets,
		Modal,
		DateBarSub,
		DayViewSub,
		WeekViewSub,
		FavoritesSub,
		LoadingSpinner
	},
	mixins: [
		baseComponentMixin
	],
	data() {
		return {
			isMobileApp: false,
			isInlineMode: false,
			isProspectMode: FrameworkService.isProspectExperience(),
			classesUrl: FrameworkService.classesUrl(),
			facilitiesUrl: FrameworkService.facilitiesUrl(),
			courtsUrl: FrameworkService.courtsUrl(),
			query: {
				viewMode: null,
				selectedDate: null
			},
			viewMode: null,
			windowWidth: 0,
			lgBreakpoint: parseInt(import.meta.env.VITE_LG_BREAKPOINT,10),
			eventData: null,
			eventDataDay: null,
			selectedDate: null,
			selectedDateIndex: 0,
			maxDay: null,
			days: [],
			filtersVisible: false,
			filterTypes: [],
			searchFilters: null,
			searchChiclets: [],
			queryFilters: [],
			facetData: null,
			savedSearchName: '',
			savedSearchFilters: [],
			urlError: false,
			favoritesVisible: false,
			favorites: [],
			isPrintingWeekView: false,
			searchType: '',
			classSchedulesFaqUrl: '',
			loginUrl: '',
			inlineMode: {
				maxHeight: 500
			},
			prospect: {
				isClubPage: false,
				headingPrefix: '',
				clubName: null,
				clubs: []
			},
			classSummary: {
				className: '',
				classDescription: ''
			}
		};
	},
	computed: {
		isWeekView: function() {
			return this.viewMode === 'week';
		},
		isDayView: function() {
			return this.viewMode === 'day';
		},
		showViewToggles: function() {
			return this.windowWidth >= this.lgBreakpoint;
		},
		skipLinkText: function() {
			return this.eventDataDay.day === momentProvider().format('YYYY-MM-DD') ? 'Skip to up next class' : 'Skip to first class';
		}
	},
	watch: {
		windowWidth: function(newWidth) {
			if (newWidth >= this.lgBreakpoint && (!this.query.viewMode || this.query.viewMode === 'week' || this.isInlineMode)) {
				this.setViewMode('week');
			} else {
				this.setViewMode('day');
			}
		}
	},
	mounted() {
		this.isInlineMode = (StateService.authoredSettings.inlineMode || this.isProspectMode) ? true : false;
		this.isMobileApp = Helpers.isMobileApp();
		const stores = this.isProspectMode
			? ['ClubStore', 'ClubsStore', 'UrlStore', 'ProspectProfileStore']
			: ['ClubStore', 'ClubsStore', 'UrlStore', 'ProfileStore'];

		this.getFrameworkData(this.completeMounting, stores);
	},
	destroyed() {
		window.removeEventListener('resize', this.handleResize);
	},
	methods: {
		completeMounting() {
			this.loginUrl = FrameworkService.globalUrl('loginResource');
			this.classSchedulesFaqUrl = FrameworkService.globalUrl('faqSchedules');

			if (this.isInlineMode) {
				if (this.isProspectMode) {
					this.prospect.isClubPage = window.lt.club ? true : false;
					this.prospect.clubName = FrameworkService.data('prospect.clubName'); // Note: Will always be the same as club.name (in non-isLocalDev environments)
					this.prospect.headingPrefix = StateService.authoredSettings.headingPrefix ? StateService.authoredSettings.headingPrefix : 'Classes at';
				}

				// Populate filterTypes based on authored settings
				if (StateService.authoredSettings.scheduleFilters) {
					this.filterTypes = (StateService.authoredSettings.scheduleFilters instanceof Array) ? StateService.authoredSettings.scheduleFilters : [ StateService.authoredSettings.scheduleFilters ];
				}

				// Inline Mode authored override for height
				this.inlineMode.maxHeight = StateService.authoredSettings.maxHeight || 500;

				// Populate query string with authored Inline Mode preselected filters (authored settings can be either strings or arrays, but setQueryStringKey handles either)
				// Also add corresponding filterTypes in case they weren't included in authoredSettings.scheduleFilters
				if (StateService.authoredSettings.preselectedInterests) {
					StateService.setQueryStringKey('interest', StateService.authoredSettings.preselectedInterests);
					this.filterTypes = _.union(this.filterTypes, [FilterTypes.interest]);
				}
				if (StateService.authoredSettings.preselectedClassNames) {
					StateService.setQueryStringKey('departmentDescription', StateService.authoredSettings.preselectedClassNames);
					this.filterTypes = _.union(this.filterTypes, [FilterTypes.className]);
				}
				if (StateService.authoredSettings.preselectedTimesOfDay) {
					StateService.setQueryStringKey('timeOfDay', StateService.authoredSettings.preselectedTimesOfDay);
					this.filterTypes = _.union(this.filterTypes, [FilterTypes.timeOfDay]);
				}
				if (StateService.authoredSettings.preselectedAges) {
					StateService.setQueryStringKey('age', StateService.authoredSettings.preselectedAges);
					this.filterTypes = _.union(this.filterTypes, [FilterTypes.age]);
				}
				if (StateService.authoredSettings.preselectedSkillLevels) {
					StateService.setQueryStringKey('skillLevel', StateService.authoredSettings.preselectedSkillLevels);
					this.filterTypes = _.union(this.filterTypes, [FilterTypes.skillLevel]);
				}
				if (StateService.authoredSettings.preselectedIntensities) {
					StateService.setQueryStringKey('intensity', StateService.authoredSettings.preselectedIntensities);
					this.filterTypes = _.union(this.filterTypes, [FilterTypes.intensity]);
				}
				if (StateService.authoredSettings.preselectedFreeOnly) {
					StateService.setQueryStringKey('freeOnly', StateService.authoredSettings.preselectedFreeOnly);
					this.filterTypes = _.union(this.filterTypes, [FilterTypes.freeOnly]);
				}
			} else {
				if (document.getElementsByClassName('main-mylt').length > 0) {
					document.getElementsByClassName('main-mylt')[0].style.overflow = 'visible'; // Override overflow: hidden on member site to prevent multiselects getting cut off at footer (Note: will need to be conditional for inline mode)
				}

				this.filterTypes = [
					FilterTypes.interest,
					FilterTypes.className,
					FilterTypes.timeOfDay,
					FilterTypes.age,
					FilterTypes.skillLevel,
					FilterTypes.intensity,
					FilterTypes.instructor,
					FilterTypes.location,
					FilterTypes.freeOnly
				];
			}

			if (StateService.getQuery('teamMemberView') === 'true') {
				this.filterTypes.push(FilterTypes.resource);
			}

			const personalization = FrameworkService.data('profile.personalization');
			this.favorites = personalization && personalization.schregSavedSearches ? personalization.schregSavedSearches : [];
			this.maxDay = momentProvider().add(import.meta.env.VITE_MAX_DAYS_CLASSES_FORWARD, 'days');

			if (!this.isInlineMode) {
				this.query.viewMode =
					StateService.getQuery('mode')
					|| SessionService.getValue('classSchedules.mode');
			}

			this.setSelectedDate();

			window.addEventListener('resize', this.handleResize);
			this.handleResize();

			// Listen for collapse events to update query string, etc.
			$('#favoritesSub').on('show.bs.collapse', this.handleFavoritesShow);
			$('#favoritesSub').on('hide.bs.collapse', this.handleFavoritesHide);

			const savedSearchQuery = StateService.getQuery('savedSearch');
			const savedSearch = savedSearchQuery ? _.find(this.favorites, ['name', savedSearchQuery]) : null;
			if (savedSearch) {
				this.handleLoadSavedSearch(savedSearch);
			} else {
				this.queryFilters = FilteringService.getSelectedFiltersFromQuery(this.filterTypes);
				this.searchFilters = FilteringService.getInitialFilters(FilteringServicePages.classSchedules, this.queryFilters, this.filterTypes);
				this.searchChiclets = this.searchFilters.chiclets;
				this.searchType = 'Initial';
				this.loadClasses();
			}
		},

		loadClasses() {
			this.isWaiting = true;

			if (window.location.href.length > 2048) {
				this.urlError = true;
				this.isWaiting = false;
				return;
			}

			this.urlError = false;
			this.getSchedules();
		},

		getSchedules() {
			const searchDateStart = this.isPrintingWeekView ? this.selectedDate.clone().startOf('week').day(1) : this.selectedDate.clone().startOf('week');
			const searchDateEnd = searchDateStart.clone().add(7, 'days').startOf('day');

			if (this.isProspectMode) {
				if (!_.includes(this.searchFilters.facets, 'location.name')) {
					this.searchFilters.facets.push('location.name');
				}
				this.searchFilters.locations = [this.prospect.clubName];
				this.searchFilters.chiclets = _.reject(this.searchFilters.chiclets, ['type', 'location']); // Remove any old location chiclets
				this.searchFilters.chiclets.push({
					name: this.prospect.clubName,
					value: this.prospect.clubName,
					type: 'location',
					disabled: true // Home club is always disabled
				});
				this.searchChiclets = this.searchFilters.chiclets;
			}

			UxSchedulesApi
				.searchClasses(searchDateStart.format('l'), searchDateEnd.format('l'), this.searchFilters, 1)
				.then(response => {
					if (!response.ok) {
						// TODO: error handling
						this.days = [];
						this.eventDataDay = null;
						this.isWaiting = false;
						return; // Note that this.facetData doesn't change, so it doesn't trigger rebuildQueryFilters
					}

					var paging = JSON.parse(response.headers['x-pagination'] || '{"pages":1}');
					this.$nextTick(() => {
						this.eventData = {
							days: response.data.results
						};
						AnalyticsService.classAnalytics(response, this.searchFilters, this.viewMode, this.searchType, this.filtersVisible);
						this.facetData = response.data.filters;
						this.isWaiting = paging.pages > 1;

						const _filter = FilteringService.build(FilterTypes.location, this.facetData, 'ltf-class-schedules-filter-', [], [], true);
						this.prospect.clubs = _filter.groups;

						// Listen for collapse events to update query string, etc. - here so that SearchFilters has mounted (due to getting facetData)
						this.$nextTick(() => {
							$('#filtersSub').on('show.bs.collapse', this.handleFiltersShow);
							$('#filtersSub').on('hide.bs.collapse', this.handleFiltersHide);
						});

						if (this.isPrintingWeekView) {
							let viewModeToReturnTo = this.viewMode;
							this.setDayData(1);
							this.viewMode = 'week';
							setTimeout(() => {
								window.print();
								this.isPrintingWeekView = false;
								this.viewMode = viewModeToReturnTo;
								this.loadClasses();
							}, 200);
						} else {
							this.setDayData(0);
							this.eventDataDay = this.days[this.selectedDateIndex].eventData; // (Not needed for Print Week)
						}
					});

					if (paging.pages > 1) {
						var promises = [];
						for (let i = 2; i <= paging.pages; i++) {
							promises.push(
								UxSchedulesApi
									.searchClasses(searchDateStart.format('l'), searchDateEnd.format('l'), this.searchFilters, i)
									.then(page => page.data.results)
							);
						}

						Promise.all(promises).then(pages => {
							var flat = _.orderBy(_.flatMap(pages, page => _.flatMap(page, result => result)), o => moment(o.day));

							_.forEach(flat, function(result) {
								var existing = _.find(response.data.results, function(r) {
									return r.day === result.day;
								});

								if (existing) {
									_.forEach(result.dayParts, function(part) {
										var existingDayPart = _.find(existing.dayParts, function(d) {
											return d.name === part.name;
										});

										if (existingDayPart) {
											_.forEach(part.startTimes, function(time) {
												var existingStartTime = _.find(existingDayPart.startTimes, function(t) {
													return t.time === time.time;
												});

												if (existingStartTime) {
													existingStartTime.activities = existingStartTime.activities.concat(time.activities);
												} else {
													existingDayPart.startTimes.push(time);
												}
											});
										} else {
											existing.dayParts.push(part);
										}
									});
								} else {
									response.data.results.push(result);
								}
							});

							this.$nextTick(() => {
								this.eventData = {
									days: response.data.results
								};
								this.setDayData(0);
								this.eventDataDay = this.days[this.selectedDateIndex].eventData;
								this.isWaiting = false;
							});
						});
					}
				});
		},

		setDayData(startDayIndex) {
			const days = [];
			const dayDataStart = startDayIndex ? this.selectedDate.clone().startOf('week').day(startDayIndex) : this.selectedDate.clone().startOf('week');

			let currMonth = '';
			let currDay = 0;

			for (let i=0; i < 7; i++) {
				const day = dayDataStart.clone().add(i, 'days');

				let dayData = {
					day: day.format('YYYY-MM-DD'),
					total: 0
				};

				const isPastMaxDay = day.diff(this.maxDay, 'days') > 0;

				if (this.eventData.days && this.eventData.days.length > currDay && this.eventData.days[currDay].day === dayData.day && !isPastMaxDay) {
					dayData = this.eventData.days[currDay];
					currDay++;
				}

				let thisMonth = day.format('MMMM').toUpperCase();
				let thisMonthAbbr = day.format('MMM').toUpperCase();
				if (currMonth === thisMonth) thisMonth = thisMonthAbbr = '';
				else currMonth = thisMonth;

				days.push({
					index: i,
					eventData: dayData,
					selected: i === this.selectedDateIndex,
					noResults: dayData.total === 0,
					today: dayData.day === momentProvider().format('YYYY-MM-DD'),
					value: dayData.day,
					display: {
						month: thisMonth,
						monthAbbr: thisMonthAbbr,
						dayName: day.format('ddd'),
						dayLetter: day.format('ddd').substring(0,1),
						dayNameFull: day.format('dddd'),
						dayNum: day.format('D'),
						total: dayData.total,
						ariaDate: day.format('dddd, MMMM D')
					}
				});
			}
			this.days = days;
		},

		setViewMode(mode) {
			this.query.viewMode = this.viewMode = mode;
			StateService.setQueryStringKey('mode', mode);
			SessionService.setValue('classSchedules.mode', mode);
		},

		setSelectedDate(selectedDate) {
			if (!selectedDate) {
				selectedDate =
					StateService.getQuery('selectedDate')
					|| moment.parseZone(momentProvider().startOf('day')).format('YYYY-MM-DD');
			}

			this.selectedDate = Helpers.parseDateToMoment(selectedDate).startOf('day'); // Will always be midnight in the local time zone

			if (this.selectedDate.clone().startOf('week').isBefore(momentProvider().startOf('week'))) {
				this.selectedDate = momentProvider().startOf('day'); // Too far in the past
			}

			if (this.selectedDate.isAfter(this.maxDay.clone().endOf('week'))) {
				this.selectedDate = momentProvider().startOf('day'); // Too far in the future
			}

			this.selectedDateIndex = this.selectedDate.day();
			this.query.selectedDate = this.selectedDate.format('YYYY-MM-DD');
			StateService.setQueryStringKey('selectedDate', this.query.selectedDate);
			// SessionService.setValue('classSchedules.selectedDate', this.query.selectedDate); // Commented out to enable URLs without query strings to default to today
		},

		handleResize() {
			this.windowWidth = window.innerWidth;
		},

		handleDayChanged(selectedDate) {
			this.setSelectedDate(selectedDate);
			this.eventDataDay = this.days[this.selectedDateIndex].eventData;
		},

		handleWeekChanged(selectedDate) {
			this.setSelectedDate(selectedDate);
			this.loadClasses();
		},

		handleFavoritesShow() {
			if (!this.userKnown && !this.favoritesVisible) { // TODO: Jenn checking with Ryan if they want to still show notification (if not, this block can be deleted)
				var url = FrameworkService.globalUrl('loginResource');
				NotificationService.info('<p><strong>Please <a href="' + url + '">log in</a> to view your Favorites.</strong></p>').trigger();
			}
			this.favoritesVisible = true;
			StateService.setQueryStringKey('showFavorites', true);
		},

		handleFavoritesHide() {
			this.favoritesVisible = false;
			StateService.removeQueryStringKey('showFavorites'); // Note: Setting to false will not have the desired effect — it's based on the existence of the parameter, not the value
		},

		handleFiltersShow() {
			this.filtersVisible = true;
			StateService.setQueryStringKey('showFilters', true);
		},

		handleFiltersHide() {
			this.filtersVisible = false;
			StateService.removeQueryStringKey('showFilters'); // Note: Setting to false will not have the desired effect — it's based on the existence of the parameter, not the value
		},

		handleFiltersChanged(updatedFilters, updateSource) {
			this.removeSavedSearchFilter(updateSource.type, updateSource.value);
			this.savedSearchName = '';
			let filterTypesToUse = this.isProspectMode ? _.union(this.filterTypes, [FilterTypes.location]) : this.filterTypes;
			this.searchFilters = FilteringService.getUpdatedFilters(FilteringServicePages.classSchedules, updatedFilters, filterTypesToUse);
			this.searchChiclets = this.searchFilters.chiclets;
			this.searchType = 'Filter Change';
			this.loadClasses();
		},

		handleRemovedChiclet(chiclet) {
			this.removeChiclet(chiclet);
		},

		handleClearAllChiclets() {
			for (const filterType of this.filterTypes) {
				StateService.removeQueryStringKey(filterType);
			}
			window.location.reload();
		},

		handleProspectClubChanged() {
			if (!Helpers.isDevOrUi()) {
				const club = FrameworkService.clubByName(this.prospect.clubName);
				if (club && club.id) {
					LtFramework.actions.updateProspectClub(club.id);
				}
			}
			this.loadClasses();
		},

		removeChiclet(chiclet) {
			this.$refs.filtersSub.deselectFilterValue(chiclet.type, chiclet.value);
		},

		removeSavedSearchFilter(filterType, filterValue) {
			const idx = _.findIndex(this.savedSearchFilters, item => {
				return item.type === filterType && item.value === filterValue;
			});
			if (idx > -1) this.savedSearchFilters.splice(idx, 1);
		},

		handleRebuildQueryFilters() {
			// Validate the filters passed in the query string
			const invalid = this.$refs.filtersSub.validateQueryValues(this.queryFilters);
			if (invalid.length > 0) {
				const chics = [];
				for (const chic of this.searchChiclets) {
					// Add valid filters from the query string to the chiclet bar
					if (!invalid.includes(chic.value)) {
						chics.push(chic);
					}
				}
				this.searchChiclets = chics;
			}
			this.queryFilters = [];

			// Remove everything from the query string
			for (const filterType of this.filterTypes) {
				StateService.removeQueryStringKey(filterType);
			}
			StateService.removeQueryStringKey('savedSearch');

			// Rebuild the query string
			if (this.savedSearchName) {
				StateService.setQueryStringKey('savedSearch', this.savedSearchName);
			} else {
				this.$nextTick(() => {
					const query = this.$refs.filtersSub.getQueryStringValues();
					for (const item of query) {
						StateService.setQueryStringKey(item.name, item.values);
					}
				});
			}

			if (StateService.getQuery('showFavorites')) $('#favoritesSub').collapse('show');
			if (StateService.getQuery('showFilters')) $('#filtersSub').collapse('show');
		},

		// Use this method to load a saved search into the component.
		handleLoadSavedSearch(savedSearchToLoad, keepPanelsOpen) {
			this.savedSearchName = savedSearchToLoad.name;
			this.savedSearchFilters = FilteringService.getSelectedFiltersFromSaved(this.filterTypes, savedSearchToLoad.filters);
			this.searchFilters = FilteringService.getInitialFilters(FilteringServicePages.classSchedules, this.savedSearchFilters, this.filterTypes);
			this.searchChiclets = this.searchFilters.chiclets;
			if (!keepPanelsOpen) {
				$('#favoritesSub').collapse('show');
				$('#filtersSub').collapse('show');
			}
			this.searchType = 'Favorite';
			this.loadClasses();
		},

		handleCreateFavorite() {
			$('#favoritesSub').collapse('show');
			this.$refs.favoritesSub.createNewFavorite(this.$refs.filtersSub.exportSave());
		},

		handleFavoritesUpdated(searchToLoad, id) {
			if (id) {
				const _updates = _.cloneDeep(this.favorites);
				_.remove(_updates, ['id', id]);
				if (searchToLoad) _updates.push(searchToLoad);
				this.favorites = _updates;
				FrameworkService.updateSavedSearches(_updates, this.creatingNew ? searchToLoad : null); // Fire and forget
			}

			if (searchToLoad) {
				this.handleLoadSavedSearch(searchToLoad, true);
			} else {
				this.savedSearchName = '';
				this.handleRebuildQueryFilters();
				this.savedSearchFilters = [];
			}
		},

		skipToTime() {
			// TODO: Disabled auto-scroll until notification center solution is in place (ESR-3496)
			// this.$refs.dayView.scrollPage(true);
		},

		triggerPrint() {
			this.isPrintingWeekView = true;
			this.loadClasses();
		},

		handleClassClicked(clickEvent, className, classDescription) {
			if (this.isProspectMode) {
				clickEvent.preventDefault();
				this.classSummary.className = className;
				this.classSummary.classDescription = classDescription ? classDescription : 'Additional Details Coming Soon';
				this.$refs.classSummaryModal.show();
			}
		}
	}
});
</script>

<style lang="scss" scoped>
@import "@/common/styles/panels.scss";
@import "@/common/styles/titleSection.scss";
@import "@/common/styles/loading.scss";
@import "styles/classSchedules.scss";
</style>
